import { multiplier } from "@local/extensions/utilities/scales.js";

// A correct overlay normally sits WELL inside its clip — the clip includes
// dimension strings and the mark text around the frame — so leaving the box
// by more than 5% of its dimension already means the Frame Scale is probably
// wrong. One exported constant so tuning is a one-line change: the slack on
// each axis is (OVERSIZE_TOLERANCE - 1) × that clip dimension.
export const OVERSIZE_TOLERANCE = 1.05;

/**
 * Does this built frame's overlay escape its clip boundary — the telltale
 * of a wrong Frame Scale? The measured rect is `cache.bbox` — the rendered
 * shape, width/height offsets included — because that is what the canvas
 * draws (and what the drag clamp confines); `cache.width_in`/`height_in`
 * stay the built-ness gate, because Polyface substitutes 48in defaults into
 * the bbox of an UNBUILT frame, so the bbox alone cannot gate. In clip
 * pixels (bottom-left basis, the same math as everywhere else) the rect
 * runs from `(-drawing_offset + bbox.min) × 300 / m` across the bbox size;
 * the warning fires when it leaves `[0..clip.width] × [0..clip.height]` by
 * more than the slack. An overlay larger than the clip can never fit, so
 * the pure-size case needs no separate check. Returns `{ ratio }` — the
 * worst axis's `(dimension + overflow) / dimension`, so `ratio > TOLERANCE`
 * keeps the single tunable constant — or `null`.
 *
 * Bounds-based IS drag-stable: the canvas drag clamp keeps a sub-clip
 * bbox inside the clip (Document.svelte's drag handler clamps the offset
 * against the same `cache.bbox`), and an oversized overlay can never be
 * in-bounds at all — so dragging cannot toggle this warning. (Size-only
 * was originally chosen for drag stability; that concern was moot.)
 *
 * Anything missing or unparsable — no clip, no scale, a custom scale
 * `multiplier()` rejects, an unbuilt frame, a missing bbox, zero clip
 * dimensions, a non-finite anything — is `null`, never a throw or NaN. A
 * missing `drawing_offset` is `{0, 0}`, not a null path.
 */
export function overlayOversize(item) {
  const clip = item?.drawing?.clip;
  const bbox = item?.cache?.bbox;
  if (!clip?.drawing_scale || !bbox) return null;
  if (!item.cache.width_in || !item.cache.height_in) return null;
  if (!clip.width || !clip.height) return null;

  let m;
  try {
    m = multiplier(clip.drawing_scale);
  } catch {
    return null;
  }
  if (!m) return null;

  const x0 = ((-(clip.drawing_offset?.x ?? 0) + (bbox.xmin ?? 0)) * 300) / m;
  const y0 = ((-(clip.drawing_offset?.y ?? 0) + (bbox.ymin ?? 0)) * 300) / m;
  const overflowX = Math.max(0, -x0, x0 + (bbox.width * 300) / m - clip.width);
  const overflowY = Math.max(
    0,
    -y0,
    y0 + (bbox.height * 300) / m - clip.height,
  );

  const ratio = Math.max(
    (clip.width + overflowX) / clip.width,
    (clip.height + overflowY) / clip.height,
  );
  if (!Number.isFinite(ratio)) return null;
  return ratio > OVERSIZE_TOLERANCE ? { ratio } : null;
}
